// #region Imports

import * as React from 'react';

// #endregion Imports

// #region Types

export type FormatType = 'bold' | 'italic' | 'underline';

export interface TextNode {
    type: 'text';
    content: string;
}

export interface FormatNode {
    type: FormatType;
    children: FormatChild[];
}

export type FormatChild = TextNode | FormatNode;

export interface FormatTag {
    open: string;
    close: string;
}

export const FORMAT_TAGS: { [key in FormatType]: FormatTag } = {
    bold: { open: '<F>', close: '</F>' },
    italic: { open: '<K>', close: '</K>' },
    underline: { open: '<U>', close: '</U>' },
};

const TAG_LIST: Array<{ type: FormatType } & FormatTag> = [
    { type: 'bold', ...FORMAT_TAGS.bold },
    { type: 'italic', ...FORMAT_TAGS.italic },
    { type: 'underline', ...FORMAT_TAGS.underline },
];

// #endregion Types

// #region Parsing

interface ParseResult {
    nodes: FormatChild[];
    index: number;
    closed: boolean;
}

// Recursive-descent parse so different-type tags may legitimately nest (e.g. bold
// containing italic, produced by a user combining toolbar/keyboard formatting on the
// same selection). Same-type tags never nest — the editor never produces that — and if
// an opening tag's matching close is never found (unclosed, or "stolen" by a same-type
// tag nested inside it), that whole opening tag falls back to literal text rather than
// throwing or dropping content, by design: it is what makes malformed/hand-edited
// strings render safely rather than break the parser.
function parseChildren(text: string, index: number, closeTag: string | null): ParseResult {
    const nodes: FormatChild[] = [];
    let textStart = index;

    const flushText = (end: number) => {
        if (end > textStart) {
            nodes.push({ type: 'text', content: text.slice(textStart, end) });
        }
    };

    while (index < text.length) {
        if (closeTag && text.startsWith(closeTag, index)) {
            flushText(index);
            return { nodes, index: index + closeTag.length, closed: true };
        }

        const tag = TAG_LIST.find(t => text.startsWith(t.open, index));
        if (tag) {
            const inner = parseChildren(text, index + tag.open.length, tag.close);
            if (inner.closed) {
                flushText(index);
                nodes.push({ type: tag.type, children: inner.nodes });
                index = inner.index;
                textStart = index;
                continue;
            }
        }

        index++;
    }

    flushText(text.length);
    return { nodes, index, closed: false };
}

export function parseNoteText(text: string): FormatChild[] {
    if (!text) {
        return [];
    }
    return parseChildren(text, 0, null).nodes;
}

// Strips the <F>/<K>/<U> tags and returns just the underlying text, for contexts that
// need a plain string rather than markup – e.g. aria-labels, which must not read raw tag
// characters aloud.
export function noteTextToPlainText(text: string): string {
    const flatten = (nodes: FormatChild[]): string =>
        nodes.map(node => node.type === 'text' ? node.content : flatten(node.children)).join('');
    return flatten(parseNoteText(text));
}

// #endregion Parsing

// #region Rendering

export function escapeHtml(raw: string): string {
    return raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const REACT_TAGS: { [key in FormatType]: 'strong' | 'em' | 'u' } = {
    bold: 'strong',
    italic: 'em',
    underline: 'u',
};

function renderNodesToReact(nodes: FormatChild[], keyPrefix: string): React.ReactNode[] {
    return nodes.map((node, index) => {
        const key = `${keyPrefix}-${index}`;
        if (node.type === 'text') {
            return <React.Fragment key={key}>{node.content}</React.Fragment>;
        }
        const Tag = REACT_TAGS[node.type];
        return <Tag key={key}>{renderNodesToReact(node.children, key)}</Tag>;
    });
}

export function noteTextToReact(text: string, keyPrefix: string = 'seg'): React.ReactNode[] {
    return renderNodesToReact(parseNoteText(text), keyPrefix);
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function renderTextHtml(raw: string, highlightQuery?: string): string {
    if (!highlightQuery || !highlightQuery.trim()) {
        return escapeHtml(raw);
    }

    const pattern = new RegExp(`(${escapeRegExp(highlightQuery)})`, 'gi');
    return raw
        .split(pattern)
        .map((part, index) => index % 2 === 1
            ? `<span class="queryMatchHightlight">${escapeHtml(part)}</span>`
            : escapeHtml(part))
        .join('');
}

const HTML_TAGS: { [key in FormatType]: string } = {
    bold: 'strong',
    italic: 'em',
    underline: 'u',
};

function renderNodesToHtml(nodes: FormatChild[], tags: { [key in FormatType]: string }, highlightQuery?: string): string {
    return nodes.map(node => {
        if (node.type === 'text') {
            return renderTextHtml(node.content, highlightQuery);
        }
        const tag = tags[node.type];
        return `<${tag}>${renderNodesToHtml(node.children, tags, highlightQuery)}</${tag}>`;
    }).join('');
}

export function noteTextToHtml(text: string, highlightQuery?: string): string {
    return renderNodesToHtml(parseNoteText(text), HTML_TAGS, highlightQuery);
}

// <b>/<i>/<u> specifically — these are the tags the browser's own execCommand('bold'|…)
// produces/recognizes, so seeding the contentEditable surface with them keeps
// document.queryCommandState in sync with what's actually on screen. Display rendering
// (view mode, lists, print) uses noteTextToHtml's <strong>/<em>/<u> instead, which are
// the semantically correct tags for read-only output.
const EDITABLE_TAGS: { [key in FormatType]: string } = {
    bold: 'b',
    italic: 'i',
    underline: 'u',
};

export function noteTextToEditableHtml(text: string): string {
    return renderNodesToHtml(parseNoteText(text), EDITABLE_TAGS);
}

// #endregion Rendering

// #region DOM serialization

const DOM_TAG_TYPE: { [tagName: string]: FormatType } = {
    B: 'bold',
    STRONG: 'bold',
    I: 'italic',
    EM: 'italic',
    U: 'underline',
};

// Walks the live contentEditable DOM and serializes it back into the <F>/<K>/<U> tag
// string. Unrecognized wrapper elements (e.g. a <span> some browsers insert while
// editing) are unwrapped rather than dropped — only their formatting is lost, not their
// text. A DIV/P is treated as a line break since some browsers wrap each new line in its
// own block; defaultParagraphSeparator is set to 'br' on the editable element so this
// path is rarely hit in practice.
export function domToNoteText(root: Node): string {
    let result = '';

    const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.nodeValue || '';
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        const el = node as HTMLElement;

        if (el.tagName === 'BR') {
            result += '\n';
            return;
        }

        if (el.tagName === 'DIV' || el.tagName === 'P') {
            if (result.length > 0 && !result.endsWith('\n')) {
                result += '\n';
            }
            el.childNodes.forEach(walk);
            return;
        }

        const formatType = DOM_TAG_TYPE[el.tagName];
        if (formatType) {
            const { open, close } = FORMAT_TAGS[formatType];
            const before = result.length;
            el.childNodes.forEach(walk);
            // Only emit the tag pair if it actually wrapped something — avoids littering
            // the saved string with empty <F></F> left behind by browser formatting state.
            if (result.length > before) {
                result = result.slice(0, before) + open + result.slice(before) + close;
            }
            return;
        }

        el.childNodes.forEach(walk);
    };

    root.childNodes.forEach(walk);
    return result;
}

// #endregion DOM serialization
